import { numberFormat } from './Currency'

/**
 * Distributes a payment amount across a set of selected invoices.
 *
 * Invoices are sorted by fecha ASC (oldest first). Each invoice is filled
 * fully before moving to the next. The last covered invoice receives whatever
 * remainder is left so floating-point drift never leaves an unallocated cent.
 *
 * @param {Array}  invoices  — array of invoice objects, each with:
 *                             { _id, uuid, folio, saldoPendiente, pagosAplicados }
 * @param {number} monto     — total payment amount (float)
 * @returns {Array} rows     — one entry per invoice:
 *   { facturaId, uuid, folio, importeSaldoAnterior, importePagado,
 *     importeSaldoInsoluto, numParcialidad }
 */
export function distributePayment(invoices, monto) {
    const sorted = [...invoices].sort(
        (a, b) => new Date(a.fecha) - new Date(b.fecha)
    )

    let remaining = parseFloat(monto) || 0
    const rows = []

    sorted.forEach((inv, idx) => {
        const importeSaldoAnterior = parseFloat(inv.saldoPendiente) || 0
        const numParcialidad = (inv.pagosAplicados?.length || 0) + 1
        const isLast = idx === sorted.length - 1

        let importePagado
        if (remaining <= 0) {
            importePagado = 0
        } else if (isLast) {
            // Last invoice gets the full remainder to avoid rounding drift
            importePagado = Math.min(remaining, importeSaldoAnterior)
        } else {
            importePagado = Math.min(remaining, importeSaldoAnterior)
        }

        const importeSaldoInsoluto = Math.max(0, importeSaldoAnterior - importePagado)
        remaining -= importePagado

        rows.push({
            facturaId:            inv._id,
            uuid:                 inv.uuid,
            folio:                inv.folio,
            monedaDR:             inv.moneda || 'MXN',
            equivalenciaDR:       1,
            numParcialidad,
            importeSaldoAnterior,
            importePagado,
            importeSaldoInsoluto
        })
    })

    return rows
}

/**
 * Recomputes importeSaldoInsoluto for a single manually-edited row.
 * Does not touch other rows.
 *
 * @param {Array}  rows            — current rows array
 * @param {number} changedIndex    — index of the row the user edited
 * @param {number} newImportePagado — new value entered by the user
 * @returns {Array} updated rows array (new reference)
 */
export function recalculateRow(rows, changedIndex, newImportePagado) {
    return rows.map((row, idx) => {
        if (idx !== changedIndex) return row

        const importePagado     = parseFloat(newImportePagado) || 0
        const importeSaldoInsoluto = Math.max(
            0,
            (parseFloat(row.importeSaldoAnterior) || 0) - importePagado
        )

        return { ...row, importePagado, importeSaldoInsoluto }
    })
}

/**
 * Rounds a float to 2 decimal places (same behaviour as the SAT XML spec).
 * Returns a Number, not a string.
 */
function round2(value) {
    return parseFloat(numberFormat(parseFloat(value) || 0, 2, '.', ''))
}

/**
 * Assembles the final API payload ready to POST to /api/complementos.
 *
 * All numeric fields are rounded to 2 decimal places and stored as Number.
 *
 * @param {Object} headerFields      — state from Section 1
 * @param {Array}  selectedInvoices  — invoice objects that are checked
 * @param {Array}  rows              — distribution rows (from distributePayment / recalculateRow)
 * @returns {Object} payload
 */
export function buildComplementoPayload(headerFields, selectedInvoices, rows, aplicacionesNoComplementables = []) {
    const selectedIds = new Set(selectedInvoices.map(inv => String(inv._id)))
    const selectedRows = rows.filter(r => selectedIds.has(String(r.facturaId)))

    return {
        clienteId:           headerFields.clienteId || selectedInvoices[0]?.receptor?._id || null,
        fechaPago:           headerFields.fechaPago,
        formaPago:           headerFields.formaPago,
        moneda:              headerFields.moneda,
        tipoCambio:          headerFields.moneda !== 'MXN' && headerFields.moneda !== 'XXX'
                                 ? round2(headerFields.tipoCambio)
                                 : undefined,
        monto:               round2(headerFields.monto),
        numeroOperacion:     headerFields.numeroOperacion || undefined,
        rfcEmisorCuentaOrd:  headerFields.rfcEmisorCuentaOrd || undefined,
        nombreBancoOrdExt:   headerFields.nombreBancoOrdExt || undefined,
        cuentaOrdenante:     headerFields.cuentaOrdenante || undefined,
        rfcEmisorCtaBen:     headerFields.rfcEmisorCtaBen || undefined,
        cuentaBeneficiario:  headerFields.cuentaBeneficiario || undefined,
        documentosRelacionados: selectedRows.map(r => ({
            facturaId:            r.facturaId,
            monedaDR:             r.monedaDR || 'MXN',
            folio:                r?.folio || '',
            uuid:                 r?.uuid  || '',
            equivalenciaDR:       round2(r.equivalenciaDR || 1),
            importePagado:        round2(r.importePagado),
            importeSaldoAnterior: round2(r.importeSaldoAnterior),
            importeSaldoInsoluto: round2(r.importeSaldoInsoluto),
            numParcialidad:       r.numParcialidad
        })),
        aplicacionesNoComplementables: aplicacionesNoComplementables.map(a => ({
            facturaId:       a.facturaId,
            folio:           a.folio || '',
            uuid:            a.uuid || '',
            metodoPago:      'PUE',
            importeAplicado: round2(a.importeAplicado),
            notas:           a.notas || undefined
        }))
    }
}

/**
 * Validates the complemento form state before submission.
 *
 * @param {Object} headerFields
 * @param {Array}  selectedInvoices
 * @param {Array}  rows
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateComplemento(headerFields, selectedInvoices, rows, aplicacionesNoComplementables = []) {
    const errors = []

    if (!headerFields.clienteId && !selectedInvoices[0]?.receptor?._id) {
        errors.push('No se pudo determinar el cliente. Agrega al menos una factura.')
    }

    if (!headerFields.fechaPago) {
        errors.push('La fecha de pago es requerida')
    }
    if (!headerFields.formaPago) {
        errors.push('La forma de pago es requerida')
    }
    const monto = parseFloat(headerFields.monto) || 0
    if (monto <= 0) {
        errors.push('El monto debe ser mayor a cero')
    }
    if (!selectedInvoices || selectedInvoices.length === 0) {
        errors.push('Selecciona al menos una factura')
    }

    if (selectedInvoices && selectedInvoices.length > 0 && rows.length > 0) {
        const selectedIds = new Set(selectedInvoices.map(inv => String(inv._id)))
        const selectedRows = rows.filter(r => selectedIds.has(String(r.facturaId)))

        const sumPagado = selectedRows.reduce(
            (acc, r) => acc + (parseFloat(r.importePagado) || 0),
            0
        )
        const sumNoComplementable = aplicacionesNoComplementables.reduce(
            (acc, r) => acc + (parseFloat(r.importeAplicado) || 0),
            0
        )

        if (sumPagado > monto + 0.01) {
            errors.push(
                `La suma de importes pagados PPD ($${sumPagado.toFixed(2)}) excede el monto del pago ($${monto.toFixed(2)})`
            )
        }

        if (sumPagado < monto - 0.01 && aplicacionesNoComplementables.length === 0) {
            errors.push(
                `Hay $${(monto - sumPagado).toFixed(2)} pendiente de asignar a una factura PUE válida`
            )
        }

        if (Math.abs((sumPagado + sumNoComplementable) - monto) > 0.01) {
            errors.push(
                `La suma de importes PPD y PUE ($${(sumPagado + sumNoComplementable).toFixed(2)}) no coincide con el monto del pago ($${monto.toFixed(2)})`
            )
        }

        selectedRows.forEach(r => {
            const importePagado        = parseFloat(r.importePagado) || 0
            const importeSaldoAnterior = parseFloat(r.importeSaldoAnterior) || 0
            if (importePagado > importeSaldoAnterior + 0.01) {
                errors.push(
                    `El importe pagado para la factura ${r.folio || r.facturaId} excede el saldo pendiente`
                )
            }
        })

        aplicacionesNoComplementables.forEach(a => {
            const importeAplicado = parseFloat(a.importeAplicado) || 0
            const importeDisponible = parseFloat(a.importeDisponibleNoComplementable ?? a.total) || 0

            if (!a.facturaId) {
                errors.push('Cada asignación PUE debe tener una factura')
            }
            if (importeAplicado <= 0) {
                errors.push(`El importe asignado a la factura PUE ${a.folio || a.facturaId} debe ser mayor a cero`)
            }
            if (importeAplicado > importeDisponible + 0.01) {
                errors.push(`El importe asignado a la factura PUE ${a.folio || a.facturaId} excede el disponible`)
            }
            if (a.fechaPagoValida === false) {
                errors.push(`La factura PUE ${a.folio || a.facturaId} está fuera del periodo válido para PUE`)
            }
        })
    }

    return { valid: errors.length === 0, errors }
}
